<script lang="ts" setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { getTypeMoney } from '../../../../helpers/getTypeMoneyByCountry';
import storePolicy from '@store/policy';
import { getScrollAncestors } from '../../shared/scrollUtils';

const { t } = useI18n();
const store = storePolicy();
const money = computed(() => getTypeMoney('MEXICO'));

// El shell de la app anida contenedores con overflow-y:auto por lo que
// `position: sticky` no encuentra el ancestro scrolleable correcto y la
// tarjeta queda fija en su posición inicial. La reemplazamos por un
// "sticky" manual: mientras la columna que la contiene siga en pantalla,
// la tarjeta pasa a position:fixed con el ancho/posición ya medidos.
const DESKTOP_BREAKPOINT = 960;
const TOP_OFFSET = 96;

const rootRef = ref<HTMLElement | null>(null);
const fixedStyle = ref<Record<string, string>>({});
let scrollTargets: (HTMLElement | Window)[] = [];

const updateStick = () => {
  const root = rootRef.value;
  if (!root || window.innerWidth < DESKTOP_BREAKPOINT) {
    if (Object.keys(fixedStyle.value).length) fixedStyle.value = {};
    return;
  }

  const rect = root.getBoundingClientRect();
  if (rect.top <= TOP_OFFSET) {
    fixedStyle.value = {
      position: 'fixed',
      top: `${TOP_OFFSET}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    };
  } else if (Object.keys(fixedStyle.value).length) {
    fixedStyle.value = {};
  }
};

onMounted(() => {
  scrollTargets = getScrollAncestors(rootRef.value);
  scrollTargets.forEach((target) =>
    target.addEventListener('scroll', updateStick, { passive: true }),
  );
  window.addEventListener('resize', updateStick);
  updateStick();
});

onBeforeUnmount(() => {
  scrollTargets.forEach((target) =>
    target.removeEventListener('scroll', updateStick),
  );
  window.removeEventListener('resize', updateStick);
});
</script>

<template>
  <aside ref="rootRef" class="wz-summary">
    <div class="wz-summary-card" :style="fixedStyle">
      <div class="wz-summary-title">
        {{ t('landing.wizard.detalle.resumen_titulo') }}
      </div>
      <div class="wz-summary-amount">{{ money }} {{ store.data.endPrice }}</div>
      <div class="wz-summary-iva">
        {{ t('landing.wizard.detalle.impuestos') }}
      </div>
      <div class="wz-summary-rows">
        <div class="row">
          <span>{{ t('landing.wizard.detalle.transporte') }}</span>
          <span>{{ t('landing.wizard.detalle.terrestre') }}</span>
        </div>
        <div class="row">
          <span>{{ t('landing.wizard.detalle.mercancia') }}</span>
          <span>{{ store.data?.asset?.nombre }}</span>
        </div>
        <div class="row">
          <span>{{ t('landing.wizard.detalle.suma_asegurada') }}</span>
          <span>{{ money }} {{ store.data.indemnificationLimit }}</span>
        </div>
        <div class="row">
          <span>{{ t('landing.wizard.detalle.cobertura') }}</span>
          <span>{{ t('landing.wizard.detalle.cobertura_valor') }}</span>
        </div>
        <div class="row">
          <span>{{ t('landing.wizard.detalle.monitoreo') }}</span>
          <span>{{ t('landing.wizard.detalle.monitoreo_valor') }}</span>
        </div>
      </div>
    </div>
  </aside>
</template>
